import MojitoNGen from 'mojito/ngen';
import actionsRegistry from 'core/services/content/actions-registry.js';
import reduxInstance from 'core/services/redux-instance';

const log = MojitoNGen.logger.get('RequestDataHelper');

/**
 *
 * Helper class contains functionality to request the specified data to be populated into the relevant store.
 * Typically used in controller views.
 *
 * @class RequestDataHelper
 * @memberof Mojito.Core.Services
 */
export default class RequestDataHelper {
    constructor(instanceId) {
        this.instanceId = instanceId;
        this.subscribersMap = new Map();
    }

    /**
     * Request the specified data to be populated into the relevant store.
     * Creates subscription on data changes and binds it to specified instanceId.
     *
     * @param {object} dataDescriptor - Data to populate the store with.
     * @function Mojito.Core.Services.RequestDataHelper#requestData
     */
    requestData(dataDescriptor) {
        if (!dataDescriptor.dataType) {
            log.warn('No data type specified!');
            return;
        }

        const subscribeAction = actionsRegistry.getSubscribe(dataDescriptor.dataType);
        const payload = this.instanceId
            ? { ...dataDescriptor, clientId: this.instanceId }
            : dataDescriptor;

        // Store dataTypes that client has subscribed to in order to effectively unsubscribe later on
        const subscriberDataTypes = this.subscribersMap.get(payload.clientId) || new Set();
        subscriberDataTypes.add(dataDescriptor.dataType);
        this.subscribersMap.set(payload.clientId, subscriberDataTypes);

        const { dispatch } = reduxInstance.store;
        subscribeAction && dispatch(subscribeAction(payload));
    }

    /**
     * Unsubscribes from all data, that was previously requested to specified instanceId.
     *
     * @param {string} [instanceId] - Id of subscriber instance. If not provided then instanceId which was set in constructor will be used.
     *
     * @function Mojito.Core.Services.RequestDataHelper#unsubscribeAll
     */
    unsubscribeAll(instanceId = this.instanceId) {
        const subscriberDataTypes = this.subscribersMap.get(instanceId);
        if (!subscriberDataTypes?.size) {
            return;
        }

        const { dispatch } = reduxInstance.store;
        Array.from(subscriberDataTypes)
            .map(dataType => actionsRegistry.getUnsubscribe(dataType))
            .filter(Boolean)
            .forEach(action => dispatch(action(instanceId)));

        this.subscribersMap.delete(instanceId);
    }
}
